import { encodingStatusIntervalMs, encodingStatusPhrases, statusLabels } from './config';
import { elements } from '../../dom';
import type { StatusId } from './types';

const ffmpegLogLines: string[] = [];
const maxFfmpegLogLines = 80;
let activeEncodeDuration = 0;
let lastEncodeProgressRatio = 0;
let progressAnimationFrame = 0;
let displayedProgress = 0;
let targetProgress = 0;
let encodingStatusInterval = 0;
let encodingStatusPhraseIndex = 0;
let encodeStartedAt = 0;
let etaRemainingSeconds: number | null = null;

const encodeProgressStart = 0.2;
const encodeProgressRange = 0.72;
const progressEaseFactor = 0.12;
const etaMinimumElapsedMs = 3000;
const etaMinimumProgressRatio = 0.015;
const etaSmoothingFactor = 0.28;

export function beginEncodeProgress(duration: number) {
  activeEncodeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  lastEncodeProgressRatio = 0;
  encodeStartedAt = performance.now();
  etaRemainingSeconds = null;
  hideEtaLabel();
  setProgress(encodeProgressStart);
}

export function endEncodeProgress() {
  activeEncodeDuration = 0;
  lastEncodeProgressRatio = 0;
  encodeStartedAt = 0;
  etaRemainingSeconds = null;
  hideEtaLabel();
}

export function updateEncodeProgressFromLog(message: string) {
  const duration = parseFfmpegDuration(message);
  if (duration !== null && activeEncodeDuration === 0) {
    activeEncodeDuration = duration;
  }

  const encodedSeconds = parseFfmpegEncodedTime(message);
  if (encodedSeconds !== null) {
    updateEncodeProgress(encodedSeconds);
  }
}

export function setStatus(status: StatusId) {
  elements.progressShell.hidden = status === 'ready' || status === 'done';
  if (status !== 'encoding') {
    hideEtaLabel();
  }
  if (status === 'encoding') {
    startEncodingStatusCycle();
    return;
  }

  stopEncodingStatusCycle();
  setPhaseLabel(statusLabels[status]);
  if (status === 'failed') {
    elements.progressShell.hidden = false;
  }
}

export function showFailureDetails(message: string) {
  elements.failureDetails.hidden = false;
  elements.failureDetails.open = false;
  elements.logOutput.textContent = message;
}

export function resetFailureDetails() {
  elements.failureDetails.hidden = true;
  elements.failureDetails.open = false;
  elements.logOutput.textContent = '';
}

export function rememberFfmpegLog(message: string) {
  ffmpegLogLines.push(message);
  if (ffmpegLogLines.length > maxFfmpegLogLines) {
    ffmpegLogLines.shift();
  }
}

export function resetFfmpegLog() {
  ffmpegLogLines.length = 0;
}

export function getFfmpegLogLines(): readonly string[] {
  return ffmpegLogLines;
}

export function setProgress(progress: number) {
  if (progress > 0 && progress < 1) {
    showProgressMeter();
  }
  elements.progressTrack.classList.remove('indeterminate');
  const clamped = Math.max(0, Math.min(1, progress));
  targetProgress = clamped;

  if (clamped === 0 || clamped === 1 || clamped < displayedProgress) {
    cancelProgressAnimation();
    if (clamped === 0 || clamped === 1) {
      hideEtaLabel();
    }
    displayedProgress = clamped;
    updateProgressDisplay(clamped);
    return;
  }

  if (!progressAnimationFrame) {
    progressAnimationFrame = window.requestAnimationFrame(animateProgress);
  }
}

export function hideProgressMeter() {
  cancelProgressAnimation();
  elements.progressTrack.classList.remove('indeterminate');
  elements.progressLabel.hidden = true;
  elements.progressTrack.hidden = true;
  elements.progressTrack.removeAttribute('aria-busy');
  elements.progressTrack.setAttribute('aria-valuenow', '0');
  elements.progressTrack.removeAttribute('aria-valuetext');
  hideEtaLabel();
}

export function showIndeterminateProgress() {
  cancelProgressAnimation();
  displayedProgress = 0;
  targetProgress = 0;
  hideEtaLabel();
  elements.progressShell.hidden = false;
  elements.progressLabel.hidden = true;
  elements.progressTrack.hidden = false;
  elements.progressTrack.classList.add('indeterminate');
  elements.progressTrack.setAttribute('aria-busy', 'true');
  elements.progressTrack.removeAttribute('aria-valuenow');
  elements.progressTrack.setAttribute('aria-valuetext', 'Reading file');
  elements.progressBar.style.width = '';
}

function updateEncodeProgress(encodedSeconds: number | null) {
  if (encodedSeconds === null || activeEncodeDuration <= 0) {
    return;
  }

  const nextRatio = Math.min(1, encodedSeconds / activeEncodeDuration);
  if (!Number.isFinite(nextRatio) || nextRatio <= 0) {
    return;
  }
  if (nextRatio <= lastEncodeProgressRatio) {
    return;
  }

  lastEncodeProgressRatio = nextRatio;
  setProgress(encodeProgressStart + nextRatio * encodeProgressRange);
  updateEtaLabel(nextRatio);
}

function parseFfmpegDuration(message: string) {
  const match = message.match(/Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/);
  return match ? timestampPartsToSeconds(match[1], match[2], match[3]) : null;
}

function parseFfmpegEncodedTime(message: string) {
  const outTime = message.match(/\bout_time=(\d+):(\d+):(\d+(?:\.\d+)?)/);
  if (outTime) {
    return timestampPartsToSeconds(outTime[1], outTime[2], outTime[3]);
  }

  const outTimeMicroseconds = message.match(/\bout_time_(?:ms|us)=(\d+)/);
  if (outTimeMicroseconds) {
    return Number(outTimeMicroseconds[1]) / 1_000_000;
  }

  const statsTime = message.match(/\btime=(\d+):(\d+):(\d+(?:\.\d+)?)/);
  return statsTime ? timestampPartsToSeconds(statsTime[1], statsTime[2], statsTime[3]) : null;
}

function timestampPartsToSeconds(hours: string, minutes: string, seconds: string) {
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
}

function startEncodingStatusCycle() {
  stopEncodingStatusCycle();
  encodingStatusPhraseIndex = 0;
  setPhaseLabel(encodingStatusPhrases[encodingStatusPhraseIndex]);
  encodingStatusInterval = window.setInterval(() => {
    encodingStatusPhraseIndex = (encodingStatusPhraseIndex + 1) % encodingStatusPhrases.length;
    setPhaseLabel(encodingStatusPhrases[encodingStatusPhraseIndex]);
  }, encodingStatusIntervalMs);
}

function stopEncodingStatusCycle() {
  if (encodingStatusInterval) {
    window.clearInterval(encodingStatusInterval);
    encodingStatusInterval = 0;
  }
}

function setPhaseLabel(label: string) {
  elements.phaseLabel.textContent = label;
}

function updateEtaLabel(progressRatio: number) {
  if (!encodeStartedAt || progressRatio < etaMinimumProgressRatio) {
    hideEtaLabel();
    return;
  }

  const elapsedMs = performance.now() - encodeStartedAt;
  if (elapsedMs < etaMinimumElapsedMs) {
    hideEtaLabel();
    return;
  }

  const elapsedSeconds = elapsedMs / 1000;
  const remainingSeconds = (elapsedSeconds / progressRatio) * (1 - progressRatio);
  if (!Number.isFinite(remainingSeconds) || remainingSeconds <= 1) {
    hideEtaLabel();
    return;
  }

  etaRemainingSeconds =
    etaRemainingSeconds === null ? remainingSeconds : etaRemainingSeconds * (1 - etaSmoothingFactor) + remainingSeconds * etaSmoothingFactor;
  elements.etaLabel.textContent = formatEta(etaRemainingSeconds);
  elements.etaLabel.hidden = false;
}

function hideEtaLabel() {
  elements.etaLabel.hidden = true;
  elements.etaLabel.textContent = '';
}

function formatEta(totalSeconds: number) {
  if (totalSeconds < 10) {
    return 'Less than 10 sec left';
  }

  if (totalSeconds < 60) {
    const seconds = Math.max(10, Math.round(totalSeconds / 5) * 5);
    return `About ${seconds} sec left`;
  }

  if (totalSeconds < 3600) {
    const minutes = Math.max(1, Math.round(totalSeconds / 60));
    return `About ${minutes} min left`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.round((totalSeconds % 3600) / 60);
  if (minutes === 0) {
    return `About ${hours} hr left`;
  }
  if (minutes === 60) {
    return `About ${hours + 1} hr left`;
  }
  return `About ${hours} hr ${minutes} min left`;
}

function showProgressMeter() {
  elements.progressShell.hidden = false;
  elements.progressLabel.hidden = false;
  elements.progressTrack.hidden = false;
  elements.progressTrack.removeAttribute('aria-busy');
}

function animateProgress() {
  progressAnimationFrame = 0;
  const delta = targetProgress - displayedProgress;

  if (Math.abs(delta) < 0.002) {
    displayedProgress = targetProgress;
    updateProgressDisplay(displayedProgress);
    return;
  }

  displayedProgress += delta * progressEaseFactor;
  updateProgressDisplay(displayedProgress);
  progressAnimationFrame = window.requestAnimationFrame(animateProgress);
}

function updateProgressDisplay(progress: number) {
  const percent = Math.max(0, Math.min(100, progress * 100));
  const roundedPercent = Math.round(percent);
  elements.progressBar.style.width = `${percent.toFixed(1)}%`;
  elements.progressLabel.textContent = `${roundedPercent}%`;
  elements.progressTrack.setAttribute('aria-valuenow', String(roundedPercent));
  elements.progressTrack.setAttribute('aria-valuetext', `${roundedPercent}%`);
}

function cancelProgressAnimation() {
  if (progressAnimationFrame) {
    window.cancelAnimationFrame(progressAnimationFrame);
    progressAnimationFrame = 0;
  }
}
